// src/components/adoption/AdoptionPost.jsx
"use client";

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserPlaceholderImage } from '../../utils/imageUtils';
import { getUserTypeColor } from '../../utils/userTypeUtils';
import { getBreedName, getShortAge } from '../../utils/petDataUtils';
import { useAuth } from '../../hooks/useAuth';
import { adoptionAPI } from '../../services/adoptionAPI';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';
import DeleteConfirmationModal from '../modals/DeleteConfirmation';
import './AdoptionPost.css';

const AdoptionPost = ({
  post,
  currentUser,
  onEdit,
  onDelete,
  onViewDetails,
  isHighlighted = false,
}) => {
  const { t } = useTranslation();
  const { getUserType } = useAuth();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [hasShared, setHasShared] = useState(post?.has_shared || false);

  const userType = getUserType();
  const userTypeColor = getUserTypeColor(userType);

  // Sync hasShared with post prop if it changes
  useEffect(() => {
    if (typeof post?.has_shared !== 'undefined') {
      setHasShared(post.has_shared);
    }
  }, [post?.has_shared, post?.id]);

  const handleShareToSocial = async (e) => {
    e.stopPropagation();
    if (isSharing || hasShared) return;

    setIsSharing(true);
    try {
      await adoptionAPI.shareAdoptionPost(post.id);
      setHasShared(true); // Mark as shared locally
      showSuccessToast(
        t('adoption.post.shareSuccess') ||
        'Adoption post shared to social feed successfully!'
      );
    } catch (error) {
      console.error('Error sharing adoption post:', error);

      // Check if error is due to already shared (409 Conflict)
      if (error?.response?.status === 409) {
        setHasShared(true); // Mark as shared even if error
        showErrorToast(
          t('adoption.post.alreadyShared') ||
          'You have already shared this adoption post.'
        );
      } else {
        showErrorToast(
          t('adoption.post.shareError') ||
          'Failed to share adoption post. Please try again.'
        );
      }
    } finally {
      setIsSharing(false);
    }
  };

  // Handle single or multiple pets
  const pets = post.pet_info
    ? (Array.isArray(post.pet_info) ? post.pet_info : [post.pet_info])
    : [];
  const primaryPet = pets[0] || {};
  const isAdopted = post.status === 'adopted';
  const isOwner = currentUser?.id === post.author.id;

  // Derive author subtype display (e.g. "Rescuer") from post author data
  const formatSubType = (subType) => {
    if (!subType) return null;
    return subType
      .toString()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const authorSubTypeDisplay =
    post.author?.sub_type_display_name ||
    formatSubType(post.author?.sub_type);

  // Backend returns my_request_status: "pending" | "accepted" | "rejected" | null
  const hasPendingRequest = post.my_request_status === 'pending';

  const cardClassName = [
    'adoption-post-card',
    isAdopted ? 'adopted' : '',
    isHighlighted ? 'adoption-post-card--highlighted' : '',
  ].filter(Boolean).join(' ');

  const petName =
    primaryPet.name ||
    t('adoption.post.pet.unknownName') ||
    'Unnamed pet';

  // Meta line: Breed · Age · Gender (only the parts we know)
  const metaParts = [
    getBreedName(primaryPet.breed),
    getShortAge(primaryPet),
    primaryPet.gender
      ? primaryPet.gender.toString().charAt(0).toUpperCase() +
        primaryPet.gender.toString().slice(1)
      : null,
  ].filter(Boolean);

  const openDetails = () => {
    onViewDetails && onViewDetails(post);
  };

  return (
    <div
      className={cardClassName}
      style={{ '--adoption-accent': userTypeColor }}
      onClick={openDetails}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openDetails();
        }
      }}
    >
      <div className="adoption-post-card-inner">
        {/* Image Section */}
        <div className="adoption-post-image">
          <img
            src={
              primaryPet.picture ||
              primaryPet.profile_image_url ||
              primaryPet.profile_image_path ||
              primaryPet.image ||
              primaryPet.avatar ||
              '/images/no-image-pet.png'
            }
            alt={petName}
            loading="lazy"
            onError={(e) => {
              e.target.src = '/images/no-image-pet.png';
            }}
          />

          {isAdopted && (
            <span className="adoption-post-status-badge adopted">
              {t('adoption.post.status.adopted') || 'Adopted'}
            </span>
          )}

          {/* Overlay actions: share + owner edit/delete */}
          <div className="adoption-post-overlay-actions">
            {!isAdopted && (
              <button
                type="button"
                className={`adoption-post-icon-btn adoption-post-share-btn ${hasShared ? 'is-shared' : ''}`}
                onClick={handleShareToSocial}
                disabled={isSharing || hasShared}
                title={
                  hasShared
                    ? (t('adoption.post.alreadyShared') || 'Already shared')
                    : (t('adoption.post.shareToSocial') || 'Share to social feed')
                }
              >
                {hasShared ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3"></circle>
                    <circle cx="6" cy="12" r="3"></circle>
                    <circle cx="18" cy="19" r="3"></circle>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                  </svg>
                )}
              </button>
            )}

            {isOwner && !isAdopted && (
              <>
                <button
                  type="button"
                  className="adoption-post-icon-btn adoption-post-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit && onEdit(post);
                  }}
                  title={t('adoption.post.edit') || 'Edit post'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="adoption-post-icon-btn adoption-post-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm(true);
                  }}
                  title={t('adoption.post.delete') || 'Delete post'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="adoption-post-info">
          <div className="adoption-post-info-top">
            <h3 className="adoption-post-pet-name">{petName}</h3>
            {hasPendingRequest && (
              <span className="adoption-post-pending-chip">
                {t('adoption.post.pending') || 'Pending'}
              </span>
            )}
          </div>

          {metaParts.length > 0 && (
            <p className="adoption-post-pet-meta">{metaParts.join(' · ')}</p>
          )}

          <div className="adoption-post-author">
            <img
              className="adoption-post-author-avatar"
              src={post.author?.avatar || getUserPlaceholderImage(post.author)}
              alt={post.author?.name || ''}
              onError={(e) => {
                e.target.src = getUserPlaceholderImage(post.author);
              }}
            />
            <span className="adoption-post-author-name">{post.author?.name}</span>
            {authorSubTypeDisplay && (
              <span className="adoption-post-author-subtype">
                {authorSubTypeDisplay}
              </span>
            )}
          </div>

          <button
            type="button"
            className="adoption-post-details-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openDetails();
            }}
          >
            {t('adoption.post.viewDetails') || 'View Details'}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"></path>
              <path d="M12 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          onDelete && onDelete(post.id);
          setShowDeleteConfirm(false);
        }}
        title={t('adoption.post.delete') || 'Delete Post'}
        message={t('adoption.post.deleteConfirm') || 'Are you sure you want to delete this adoption post?'}
        confirmText={t('adoption.post.delete') || 'Delete'}
        cancelText={t('common.cancel') || 'Cancel'}
      />
    </div>
  );
};

export default AdoptionPost;
